<template>
  <div class="autocomplete-select">
    <!-- country select field -->
    <BaseInput :label="label" :errors="errors" :hasIcon="props.hasIcon">
      <template v-if="isLoading">
        <SkeleLoader v-if="isLoading" lines="1" contentIsFullWidth />
      </template>
      <template v-else>
        <template v-if="props.hasIcon">
          <GlobeCircle />
        </template>

        <div
          :id="`autoCompleteSelect${autocompleteType}`"
          class="autocomplete-select__select-field"
          :class="{ isOpen: isOpen }"
          v-click-out-side="handleBlur"
        >
          <p
            :class="[
              { hidden: isFocused, hasIcon: props.hasIcon },
              'autocomplete-select__selected-item',
            ]"
          >
            {{ selectedItem }}
          </p>
          <input
            ref="searchInput"
            :type="`${type}`"
            :id="`${name}-select`"
            :name="`${name}-select`"
            :placeholder="!selectedItem ? `${placeholder}` : ''"
            autocomplete="off"
            :aria-label="`${type}`"
            :aria-expanded="isOpen"
            v-model="search"
            @input="searchList"
            @focus="searchList"
            @keydown.enter="selectFirstItem"
            @keydown.escape="handleBlur"
            tabindex="0"
          />

          <ul class="autocomplete-container" v-show="isOpen" ref="dropdownMenu">
            <li
              role="button"
              aria-labelledby="select-item"
              v-for="(item, index) in filteredList"
              :key="index"
              class="autocomplete-item"
              :class="{
                isActive: index === arrowCounter,
                disabled: item?.isDisabled,
              }"
              :tabindex="0"
              @click="setSearchValue(item)"
              @keydown.up="goToPreviousEl"
              @keydown.down="goToNextEl"
              @keydown.tab="goToNextEl"
              @keydown.enter="selectEl"
            >
              <template v-if="props.autocompleteType === 'country'">
                <div class="country-flag">
                  <country-flag :country="item.code.toLowerCase()" size="big" />
                </div>
              </template>
              <p class="autocomplete-item__text">
                <template v-if="props.autocompleteType === 'phoneCountry'">
                    <!-- <country-flag :country="item.code.toLowerCase()" size="medium" />  -->
                    <div class="country-name">{{ item.name + ` (+${item.internationalCode })`}} </div> 
                </template>

                <template
                  v-else-if="Object.getOwnPropertyDescriptor(item, 'name')"
                >
                  {{ item.name }}
                </template>

                <template v-else>
                  <slot name="selectItem" :item="item"></slot>

                  <template v-if="!$slots.selectItem">
                    {{ item }}
                  </template>
                </template>

                <pill v-if="item.isDisabled">Unsupported</pill>
              </p>
            </li>
          </ul>
          <div class="caret" @click="onClickCaret">
            <CaretDown />
          </div>
        </div>
      </template>
    </BaseInput>
  </div>
</template>

<script setup>
import { watch, onMounted, ref } from "vue";
import { whenever } from "@vueuse/core";
import CountryFlag from "vue-country-flag-next";

import useAutoCompleteSelect from "@/composables/ui/useAutoCompleteSelect.js";
import { vClickOutSide } from "@/composables/directives/useClickOutside";

import BaseInput from "@/components/ui/inputs/BaseInput.vue";
import Pill from "@/components/ui/pills/Pill.vue";
import GlobeCircle from "@/components/icons/GlobeCircle.vue";
import CaretDown from "@/components/icons/CaretDown.vue";
import SkeleLoader from "@/components/ui/loaders/SkeleLoader.vue";

const props = defineProps({
  errors: { type: String, default: "" },
  items: { type: Array, required: true },
  name: { type: String, default: "default-select" },
  label: { type: String, default: "" },
  placeholder: {
    type: String,
    default: "",
  },
  type: { type: String, required: true },
  hasIcon: { type: Boolean, default: false },
  autocomplete: { type: String, default: "" },
  autocompleteType: { type: String, default: "country" },
  isLoading: { type: Boolean, default: false },
});
const emit = defineEmits(["update:autocomplete"]);

const {
  isOpen,
  search,
  selectedItem,
  arrowCounter,
  searchList,
  filteredList,
  goToNextEl,
  selectEl,
  goToPreviousEl,
  setSearchValue,
  selectFirstItem,
  toggleSelect,
  updateCurrentList,
  handleBlur,
} = useAutoCompleteSelect(props.items, props.autocompleteType);

const searchInput = ref(null);

whenever(selectedItem, () => {
  emit("update:autocomplete", selectedItem);
});

watch(
  () => props.items,
  (newItems, prevItems) => {
    updateCurrentList(newItems);
  },
  { deep: true }
);

onMounted(() => {
  selectedItem.value = props.autocomplete;
});

const focusFirstLi = () => {
  const listEl = document.querySelector(".autocomplete-container > li");
  listEl.focus();
};

const onClickCaret = () => {
  isOpen.value = !isOpen.value;
  if (isOpen.value) {
    searchInput.value.focus();
    searchList();
  } else {
    search.value = "";
  }
};
</script>
<style lang="scss" scoped>
$dropdown-max-height: 200px;
$flag-dimension: 32px;
.autocomplete-select {
  &__select-field {
    position: relative;

    .caret {
      position: absolute;
      top: 15%;
      left: unset;
      right: 5px;
      padding: 20px;
    }

    svg {
      &.caret-down {
        // right: 15px;
        left: unset;
        transform: rotate(0deg) translate(-50%, -50%);
        transition: transform 250ms;
      }
    }

    .autocomplete-container {
      max-height: $dropdown-max-height;
      width: calc(100% - 2rem);
      z-index: 1;
      position: absolute;
      transform: translateX(-50%);
      left: 50%;
      overflow-y: scroll;
      border: var(--dropdown-border);
      background-color: var(--dropdown-background);
      border-radius: $border-radius-dropdown;
      box-shadow: 3px 2px 2px -2px rgba(0, 0, 0, 0.65);

      .autocomplete-item {
        margin: 0 auto;
        padding-block: 1.5rem;
        display: grid;
        grid-template-columns: 1fr 5fr;
        align-items: center;
        padding-left: 2rem;
        border-bottom: var(--dropdown-border);
        @include breakpoint($min-m) {
          padding-left: 4rem;
        }

        &:hover,
        &:focus-visible,
        &.isActive {
          cursor: pointer;
          width: 100%;
          background-color: var(--input-background);
          color: white;
        }

        &.disabled {
          padding-right: 4rem;

          .autocomplete-item__text {
            // account for disabled pill
            justify-content: space-between;
            width: 100%;
          }
        }

        &:last-child {
          border: none;
        }

        .country-flag {
          height: $flag-dimension;
          width: $flag-dimension;
          margin-right: 1.5rem;
          position: relative;
          display: block;
          border-radius: 50%;
          overflow: hidden;

          .flag {
            position: absolute;
            left: 50%;
            top: 50%;
            transform: translate(-50%, -50%);
          }
        }

        &__text {
          display: flex;
          .country-iso2 {
            flex: 0 1 auto;
          }
        }
      }
    }

    //states
    &.isOpen {
      svg.caret-down {
        transform: rotate(180deg) translate(50%, 50%);
      }
    }
  }

  &__selected-item {
    .isOpen & {
      display: none;
    }

    .hasIcon & {
      left: calc(2rem + 40px);
    }

    position: absolute;
    left: 2rem;
    top: 50%;
    transform: translate(0, -50%);
    @include breakpoint($max-xs) {
      left: 25%;
    }
  }
}
</style>
